import { useEffect, useState } from "react";
import { api, formatDate } from "@/lib/api";
import { openWhatsApp } from "@/lib/whatsapp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { MessageCircle, Phone, PhoneCall } from "lucide-react";

const tl = (n) => new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 }).format(Number(n || 0));
export const openContactCard = (name) => window.dispatchEvent(new CustomEvent("open-contact-card", { detail: name }));

const listText = (d) => {
  const rows = d.upcoming.map((p, i) => `${i + 1}. ${p.customer_name} · ${p.policy_type}${p.plate ? ` · ${p.plate}` : ""} · bitiş ${formatDate(p.end_date)}`);
  return `Merhaba ${d.name},\nBize yönlendirdiğiniz müşterilerden vadesi yaklaşan poliçeler:\n\n${rows.join("\n")}\n\nYenileme için bilginize sunarız. Teşekkürler.`;
};

const Stat = ({ label, value, tid }) => (
  <div className="rounded-lg border border-zinc-200 bg-white px-3 py-2" data-testid={tid}>
    <p className="text-[10px] uppercase tracking-wider text-zinc-400">{label}</p><p className="font-heading text-base font-semibold tabular text-zinc-900">{value}</p>
  </div>
);

const Upcoming = ({ list }) => (list.length === 0 ? <p className="text-sm text-zinc-400">Önümüzdeki 30 gün içinde yenileme yok.</p> : (
  <div className="divide-y divide-zinc-100 rounded-lg border border-zinc-200">
    {list.map((p) => (
      <div key={p.id} className="flex items-center justify-between gap-2 px-3 py-2" data-testid={`contact-upcoming-${p.id}`}>
        <div className="min-w-0"><p className="text-sm font-medium text-zinc-900 truncate">{p.customer_name}</p>
          <p className="text-xs text-zinc-500 truncate">{p.policy_type}{p.plate ? ` · ${p.plate}` : ""}{p.insurance_company ? ` · ${p.insurance_company}` : ""}</p></div>
        <span className={`shrink-0 text-xs font-semibold tabular ${p.days_left < 0 ? "text-rose-600" : p.days_left <= 7 ? "text-amber-600" : "text-zinc-600"}`}>
          {p.days_left < 0 ? `${-p.days_left} gün geçti` : p.days_left === 0 ? "Bugün" : `${p.days_left} gün`}</span>
      </div>
    ))}
  </div>
));

export const ContactCardHost = () => {
  const [name, setName] = useState(null);
  const [d, setD] = useState(null);
  const [phone, setPhone] = useState("");
  useEffect(() => { const h = (e) => setName(e.detail); window.addEventListener("open-contact-card", h); return () => window.removeEventListener("open-contact-card", h); }, []);
  useEffect(() => {
    if (!name) return;
    setD(null);
    api.get("/contacts/card", { params: { name } }).then((r) => { setD(r.data); setPhone(r.data.phone); }).catch(() => { toast.error("İrtibat kişisi bulunamadı"); setName(null); });
  }, [name]);

  const savePhone = async () => {
    try { const r = await api.put("/contacts", { name: d.name, phone }); setD({ ...d, phone: r.data.phone }); toast.success("Telefon kaydedildi"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); }
  };

  return (
    <Sheet open={Boolean(name)} onOpenChange={(o) => !o && setName(null)}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto p-0" data-testid="contact-card">
        <SheetHeader className="bg-[#062a20] px-6 py-5 text-left">
          <SheetTitle className="flex items-center gap-2 text-white"><PhoneCall className="h-5 w-5 text-sky-300" />{name}</SheetTitle>
          <p className="text-xs text-emerald-100/70">İrtibat Kişisi · getirdiği poliçeler ve yaklaşan yenilemeler</p>
        </SheetHeader>
        {!d ? <div className="p-6 space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div> : (
          <div className="p-6 space-y-5">
            <div className="flex items-center gap-2">
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Telefon" data-testid="contact-phone-input" className="h-9" />
              <Button size="sm" variant="outline" onClick={savePhone} disabled={phone === d.phone} data-testid="contact-phone-save" className="h-9">Kaydet</Button>
              {d.phone && <a href={`tel:${d.phone.replace(/\s/g, "")}`} data-testid="contact-call"><Button size="sm" variant="outline" className="h-9 gap-1"><Phone className="h-4 w-4" />Ara</Button></a>}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Stat label="Getirdiği müşteri" value={d.customer_count} tid="contact-customers" />
              <Stat label="Aktif poliçe" value={`${d.active_count} / ${d.policy_count}`} tid="contact-active" />
              <Stat label={`${d.year} Toplam Üretim`} value={tl(d.year_premium)} tid="contact-year-premium" />
              <Stat label={`${d.year} Net Komisyon`} value={tl(d.year_net_commission)} tid="contact-year-net" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-zinc-400">Yaklaşan yenilemeler · {d.upcoming.length}</p>
                {d.upcoming.length > 0 && (
                  <Button size="sm" disabled={!d.phone} onClick={() => openWhatsApp(d.phone, listText(d))} data-testid="contact-send-list"
                    className="h-8 gap-1.5 bg-[#25D366] hover:bg-[#1ebe5a] text-white"><MessageCircle className="h-4 w-4" />Listeyi WhatsApp'la gönder</Button>
                )}
              </div>
              {!d.phone && d.upcoming.length > 0 && <p className="text-xs text-amber-700 mb-2">Listeyi göndermek için önce telefon kaydedin.</p>}
              <Upcoming list={d.upcoming} />
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
